import * as formik from "formik";
import { useField } from "formik";
import { useCallback, useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FormRadio, radioDataType } from "../FormRadio/FormRadio";
import { Modal } from "../Modal/Modal";
import {
  convertDateStrings,
  createDeeplinkList,
  handleHourMismatch,
  notificationSchema,
} from "../NewPushNotification/utils";
import { Notification } from "../Notification/Notification";
import { ToastContent } from "../Notification/types";
import {
  NavProps,
  PushNotificationType,
} from "../_shared/pushNotifications/types";
import "./PushNotificationEdit.css";
import { isDuePastOrWithinHour } from "./internals";
import Table from "react-bootstrap/Table";
import { DeepLinkList } from "../NewPushNotification/NewPushNotification";
import { Heading, useAuthenticator } from "@aws-amplify/ui-react";
import {
  useUpdatePushNotificationMutation,
  useGetPushNotificationByIdLazyQuery,
  UpdatePushNotificationMutationVariables,
  useGetMessagesQuery,
  Messages,
} from "../../graphql/generated/graphql";
import { BackButton } from "../_shared/Buttons/BackButton";
import { NotificationStatusPill } from "../_shared/StatusPill";

export const PushNotificationEdit = () => {
  const { signOut } = useAuthenticator();
  const [updatePushNotification] = useUpdatePushNotificationMutation();
  const [getPushNotificationById] = useGetPushNotificationByIdLazyQuery();
  const { data: messagesData } = useGetMessagesQuery();
  const [notification, setNotification] = useState({} as PushNotificationType);
  const { Formik } = formik;
  const location = useLocation().state as NavProps;
  const [tmcUsersInfo, setTmcUsersInfo] = useState("");
  const [tiUsersInfo, setTiUsersInfo] = useState("");
  const [deviceOSInfo, setDeviceOSInfo] = useState<"android" | "ios" | "both">(
    "android"
  );

  const [toast, setToast] = useState<ToastContent>({
    title: "",
    description: "",
    isVisible: false,
    notificationVariant: "success",
  });

  const [showStopModal, setShowStopModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showOneHourModal, setShowOneHourModal] = useState<boolean>(false);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [pushNotificationLoaded, setPushNotificationLoaded] =
    useState<boolean>(false);

  const [isEditDisabled, setIsEditDisabled] = useState<boolean>(false);
  const [disableSubmitBtn, setDisableSubmitBtn] = useState<boolean>(false);
  const [disableStopBtn, setDisableStopBtn] = useState<boolean>(false);
  const [statusOverview, setStatusOverview] = useState<string>("");
  const [deeplinkMessages, setDeeplinkMessages] = useState(
    [] as DeepLinkList[]
  );
  const [loadingMessages, setLoadingMessages] = useState<boolean>(true);

  const handleMessages = useCallback(async () => {
    try {
      if (messagesData?.getMessages) {
        console.log(`checking messages data ${JSON.stringify(messagesData)}`);
        const deeplinkList = await createDeeplinkList(
          messagesData.getMessages as Messages[]
        );
        setDeeplinkMessages(deeplinkList);
        setLoadingMessages(false);
      }
    } catch (e) {
      console.log("handleMessages error:", e);
      // signOut or error UI if needed
    }
  }, [messagesData]);

  const { id } = useParams();
  const navigate = useNavigate();

  const gettingPushNotificationById = useCallback(async () => {
    try {
      const response = await getPushNotificationById({
        variables: { pushNotificationId: id },
        fetchPolicy: "network-only",
      });
      const pushNotificationData = response.data?.getPushNotificationById;

      console.log("pushNotificationData: ", pushNotificationData);

      setNotification({
        ...pushNotificationData,
        dueOn: handleHourMismatch(pushNotificationData.dueOn),
      });
      console.log("after we have set notification data");

      setTmcUsersInfo(pushNotificationData.tmcUsers);
      setTiUsersInfo(pushNotificationData.tiUsers);
      if (
        pushNotificationData.deviceOS === "android" ||
        pushNotificationData.deviceOS === "ios" ||
        pushNotificationData.deviceOS === "both"
      )
        setDeviceOSInfo(pushNotificationData.deviceOS);

      if (
        location.status === "STOP" ||
        location.status === "COMPLETED" ||
        isDuePastOrWithinHour(pushNotificationData.dueOn)
      ) {
        setIsEditDisabled(true);
        setStatusOverview("Push notification can no longer be edited");
      }
      setPushNotificationLoaded(true);

      setDisableStopBtn(
        location.status === "COMPLETED" || location.status === "STOP"
      );
    } catch (e) {
      console.log("getPushNotificationById error: ", e);
      setShowErrorModal(true);
    }
  }, [getPushNotificationById, id, location.status]);

  const DatePickerField = (props: formik.FieldHookConfig<DatePicker>) => {
    const [field, meta, helpers] = useField(props);
    console.log(meta); //used to fix eslint error for deployment
    return (
      <DatePicker
        selected={field.value && new Date(field.value)}
        onChange={(value) => helpers.setValue(value)}
        showMonthPicker
        showTimeInput
        showMonthDropdown
        showYearDropdown
        timeInputLabel="Time:"
        dateFormat="dd/MM/yyyy h:mm aa"
        className="datePicker"
        name="dueOn"
        disabled={isEditDisabled}
      />
    );
  };

  enum toastContent {
    "SUCCESS" = "You successfully updated the push notification",
    "ERROR" = "Failed to update the push notification",
  }

  const handleShowToast = (isSuccess: boolean) => {
    setToast({
      title: isSuccess ? "Success!" : "Error!",
      description: isSuccess ? toastContent.SUCCESS : toastContent.ERROR,
      isVisible: true,
      notificationVariant: isSuccess ? "success" : "danger",
    });

    if (isSuccess) {
      setTimeout(() => {
        navigate(-1);
      }, 3000);
    }
  };

  const handleUpdateNotification = async (values: PushNotificationType) => {
    if (isDuePastOrWithinHour(handleHourMismatch(notification.dueOn, -1))) {
      setIsEditDisabled(true);
      setStatusOverview("Push notification can no longer be edited");
      setShowOneHourModal(true);
      return false;
    }
    try {
      const time = convertDateStrings(values.dueOn);

      const variables: UpdatePushNotificationMutationVariables = {
        pushNotification: {
          ...values,
          scheduleId: notification.scheduleId,
          dueOn: time,
          tiUsers: tiUsersInfo,
          tmcUsers: tmcUsersInfo,
          deviceOS: deviceOSInfo,
        },
      };

      if (variables.pushNotification["__typename"]) {
        delete variables.pushNotification["__typename"];
      }

      console.log(
        `before update push notification call with variables ${JSON.stringify(variables)}`
      );

      const repsonse = await updatePushNotification({ variables });

      console.log(`checking update push notification response ${repsonse}`);

      if (repsonse.data.updatePushNotification) {
        console.log("update succeded");
        handleShowToast(true);
        return true;
      } else {
        console.log("update failed");
        handleShowToast(false);
        throw new Error("Something went wrong");
      }
    } catch (e) {
      console.log("handleUpdateNotification error: " + JSON.stringify(e));
      setShowErrorModal(true);
    }
  };

  const goBack = () => {
    if (showErrorModal) {
      setShowErrorModal(false);
    }
    navigate(-1);
  };

  const handleNotificationClose = () => {
    setToast({ ...toast, isVisible: false });
  };

  const stopPushNotification = async () => {
    setDisableSubmitBtn(true);
    setDisableStopBtn(true);
    try {
      const variables: UpdatePushNotificationMutationVariables = {
        pushNotification: {
          ...notification,
          dueOn: convertDateStrings(notification.dueOn),
          status: "STOP",
        },
      };

      if (variables.pushNotification["__typename"]) {
        delete variables.pushNotification["__typename"];
      }

      const { data } = await updatePushNotification({ variables });

      handleCloseStopModal();
      if (data.updatePushNotification) {
        console.log("succeeded");
        handleShowToast(true);
      } else {
        console.log("failed");
        handleShowToast(false);
        setDisableSubmitBtn(isEditDisabled);
        setDisableStopBtn(false);
      }
    } catch (e) {
      console.log("stopPushNotification error: " + JSON.stringify(e));
      setShowErrorModal(true);
      setDisableSubmitBtn(isEditDisabled);
      setDisableStopBtn(false);
      signOut();
    }
  };

  const radioTMC: radioDataType[] = [
    { radioLabel: "Yes", radioId: "yes", radioName: "tmcUsers" },
    { radioLabel: "N/A", radioId: "n/a", radioName: "tmcUsers" },
    { radioLabel: "No", radioId: "no", radioName: "tmcUsers" },
  ];

  const radioTI: radioDataType[] = [
    { radioLabel: "Yes", radioId: "yes", radioName: "tiUsers" },
    { radioLabel: "N/A", radioId: "n/a", radioName: "tiUsers" },
    { radioLabel: "No", radioId: "no", radioName: "tiUsers" },
  ];
  const radioDevice: radioDataType[] = [
    { radioLabel: "Both", radioId: "both", radioName: "deviceOS" },
    { radioLabel: "IOS Only", radioId: "ios", radioName: "deviceOS" },
    {
      radioLabel: "Android Only",
      radioId: "android",
      radioName: "deviceOS",
    },
  ];

  const handleOpenStopModal = () => {
    setShowStopModal(true);
  };

  const handleCloseStopModal = () => {
    setShowStopModal(false);
  };

  const handleOpenEditModal = () => {
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
  };

  const handleCloseOneHourModal = () => {
    setShowOneHourModal(false);
  };

  useEffect(() => {
    gettingPushNotificationById();
  }, [gettingPushNotificationById]);

  useEffect(() => {
    handleMessages();
  }, [handleMessages]);

  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // goes back if there's history, else, goes to the list
    if (window.history.length > 1) navigate(-1);
    else navigate("/push-notification");
  };

  const toastVariant = toast.title === "Error!" ? "danger" : "success";

  return (
    <Container fluid as={"div"} className="screenContainer">
      <div className="pushCard">
        <BackButton onClick={onClick} label={"Back to push notification"} />
        <Heading level={2} className="screen-title">
          Edit push notification: {notification.scheduleId}
        </Heading>

        <div style={{ marginTop: 30 }}>
          <Table responsive borderless size="sm" className="pushTable">
            <colgroup>
              <col style={{ width: 280 }} />
              <col />
            </colgroup>
            <tbody>
              <tr>
                <th scope="row" className="pushLabel">
                  Schedule Id
                </th>
                <td className="pushValue push-mono">
                  {notification.scheduleId}
                </td>
              </tr>
              <tr>
                <th scope="row" className="pushLabel">
                  Successful notifications
                </th>
                <td className="pushValue push-mono">{location.success ?? 0}</td>
              </tr>
              <tr>
                <th scope="row" className="pushLabel">
                  Failed notifications
                </th>
                <td className="pushValue push-mono">{location.failure ?? 0}</td>
              </tr>
              <tr>
                <th scope="row" className="pushLabel">
                  Users opted out
                </th>
                <td className="pushValue push-mono">
                  {location.optedOut ?? 0}
                </td>
              </tr>
              <tr>
                <th scope="row" className="pushLabel">
                  Status
                </th>
                <td className="pushValue push-mono">
                  <NotificationStatusPill value={location.status} />
                </td>
              </tr>
              {statusOverview && (
                <tr>
                  <th scope="row" className="pushLabel">
                    Status overview
                  </th>
                  <td className="pushValue push-note push-mono">
                    {statusOverview || "—"}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {pushNotificationLoaded && !loadingMessages && (
            <Formik
              validationSchema={notificationSchema}
              initialValues={notification}
              onSubmit={async (
                values: PushNotificationType,
                { setSubmitting, resetForm }
              ) => {
                setSubmitting(true);
                await handleUpdateNotification(values)
                  .then((response) => {
                    if (response === false) {
                      resetForm();
                    }
                    setDisableSubmitBtn(true);
                  })
                  .catch((e) => {
                    resetForm();
                  });
                setSubmitting(false);
                handleCloseEditModal();
              }}
            >
              {({
                handleChange,
                handleSubmit,
                values = notification,
                touched,
                errors,
                handleBlur,
                isSubmitting,
              }) => (
                <Form noValidate onSubmit={handleSubmit} id="PushForm">
                  <div className="formContainer">
                    <div className="formItem">
                      <Form.Group as={Row}>
                        <Form.Label column sm={3} className="label">
                          Notification name
                          <span id="name-hint" className="label__hint">
                            (Internal reference)
                          </span>
                        </Form.Label>
                        <Col sm={9}>
                          <Form.Control
                            name="name"
                            aria-label="Notificaton Name"
                            size="lg"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.name}
                            isValid={touched.name && !errors.name}
                            isInvalid={!!errors.name}
                          ></Form.Control>
                          <Form.Control.Feedback type="invalid">
                            {errors.name}
                          </Form.Control.Feedback>
                        </Col>
                      </Form.Group>
                    </div>
                    <div className="formItem">
                      <Form.Group as={Row}>
                        <Form.Label column sm={3} className="label">
                          Notification title
                        </Form.Label>
                        <Col sm={9}>
                          <Form.Control
                            name="title"
                            aria-label="Notificaton Title"
                            size="lg"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.title}
                            isValid={touched.title && !errors.title}
                            isInvalid={!!errors.title}
                            disabled={isEditDisabled}
                          ></Form.Control>
                          <Form.Control.Feedback type="invalid">
                            {errors.title}
                          </Form.Control.Feedback>
                        </Col>
                      </Form.Group>
                    </div>
                    <div className="formItem">
                      <Form.Group as={Row}>
                        <Form.Label column sm={3} className="label">
                          Notification body
                        </Form.Label>
                        <Col sm={9}>
                          <Form.Control
                            name="body"
                            aria-label="Notificaton Body"
                            size="lg"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.body}
                            isValid={touched.body && !errors.body}
                            isInvalid={!!errors.body}
                            as={"textarea"}
                            disabled={isEditDisabled}
                          ></Form.Control>
                          <Form.Control.Feedback type="invalid">
                            {errors.body}
                          </Form.Control.Feedback>
                        </Col>
                      </Form.Group>
                    </div>
                    <div className="formItem">
                      <Form.Group as={Row}>
                        <Form.Label column sm={3} className="label">
                          Notification deeplink
                        </Form.Label>
                        <Col sm={9}>
                          <Form.Select
                            name="deeplink"
                            aria-label="Deeplink"
                            size="lg"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            defaultValue={values.deeplink}
                          >
                            <option value={""}>No deeplinking</option>
                            {deeplinkMessages.map((message) => {
                              return (
                                <option value={`message/${message.id}`}>
                                  {message.Title}
                                </option>
                              );
                            })}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {errors.deeplink}
                          </Form.Control.Feedback>
                        </Col>
                      </Form.Group>
                    </div>
                    <Row
                      as={"div"}
                      className="formItem"
                      style={{ height: 48, alignItems: "center" }}
                    >
                      <Col xs={3} className="label">
                        <span>Due on</span>
                        <span id="due-hint" className="label__hint">
                          (time is rounded)
                        </span>
                      </Col>

                      <Col xs={9}>
                        <DatePickerField name="dueOn" />
                      </Col>
                    </Row>
                    <div className="formItem">
                      <Form.Group as={Row}>
                        <Form.Label column sm={3} className="label">
                          Throttle Rate
                          <span id="throttle-hint" className="label__hint">
                            (number of notifications sent per second)
                          </span>
                        </Form.Label>
                        <Col sm={9} style={{ margin: "auto" }}>
                          <Form.Control
                            name="throttleRate"
                            aria-label="Throttle rate"
                            size="lg"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.throttleRate}
                            isValid={
                              touched.throttleRate && !errors.throttleRate
                            }
                            isInvalid={!!errors.throttleRate}
                            disabled={isEditDisabled}
                          ></Form.Control>
                          <small className="hint">
                            We recommend 20–50 to avoid provider throttling.
                          </small>
                          <Form.Control.Feedback type="invalid">
                            {errors.throttleRate}
                          </Form.Control.Feedback>
                        </Col>
                      </Form.Group>
                    </div>
                    <div className="formItem">
                      <Form.Group as={Row} className="checkContainer">
                        <Form.Label column sm={3} className="label">
                          Automatically pause schedule?
                        </Form.Label>
                        <Col sm={9} style={{ margin: "auto" }}>
                          <Form.Check
                            type="switch"
                            name="autoPauseSchedule"
                            aria-label="Automatically pause schedule"
                            onChange={handleChange}
                            className="checkedInput"
                            disabled={isEditDisabled}
                            defaultChecked={values.autoPauseSchedule}
                          />
                        </Col>
                      </Form.Group>
                    </div>
                    <div className="formItem">
                      <Form.Group as={Row}>
                        <Form.Label column sm={3} className="label">
                          Pause sending time
                        </Form.Label>
                        <Col sm={9} style={{ margin: "auto" }}>
                          <Form.Control
                            name="pauseSendingTime"
                            aria-label="Pause sending time"
                            size="lg"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.pauseSendingTime}
                            isValid={
                              touched.pauseSendingTime &&
                              !errors.pauseSendingTime
                            }
                            isInvalid={!!errors.pauseSendingTime}
                            disabled={isEditDisabled}
                          ></Form.Control>
                          <Form.Control.Feedback type="invalid">
                            {errors.pauseSendingTime}
                          </Form.Control.Feedback>
                        </Col>
                      </Form.Group>
                    </div>
                    <div className="formItem">
                      <Form.Group as={Row}>
                        <Form.Label column sm={3} className="label">
                          Resume sending time
                        </Form.Label>
                        <Col sm={9} style={{ margin: "auto" }}>
                          <Form.Control
                            name="resumeSendingTime"
                            aria-label="Resume sending time"
                            size="lg"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.resumeSendingTime}
                            isValid={
                              touched.resumeSendingTime &&
                              !errors.resumeSendingTime
                            }
                            isInvalid={!!errors.resumeSendingTime}
                            disabled={isEditDisabled}
                          ></Form.Control>
                          <Form.Control.Feedback type="invalid">
                            {errors.resumeSendingTime}
                          </Form.Control.Feedback>
                        </Col>
                      </Form.Group>
                    </div>
                    <div className="formItem">
                      <Form.Group as={Row}>
                        <Form.Label column sm={3} className="label">
                          Application
                        </Form.Label>
                        <Col sm={9} style={{ margin: "auto" }}>
                          <Form.Control
                            name="application"
                            aria-label="application"
                            size="lg"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.application}
                            isValid={touched.application && !errors.application}
                            isInvalid={!!errors.application}
                            disabled
                          ></Form.Control>
                          <Form.Control.Feedback type="invalid">
                            {errors.application}
                          </Form.Control.Feedback>
                        </Col>
                      </Form.Group>
                    </div>
                    <div className="formItem radio-btn">
                      <FormRadio
                        radioData={radioTMC}
                        labelText="TMC users"
                        setInfo={setTmcUsersInfo}
                        defaultValue={tmcUsersInfo}
                        disabled={isEditDisabled}
                      ></FormRadio>
                    </div>
                    <div className="formItem radio-btn">
                      <FormRadio
                        radioData={radioTI}
                        labelText="TI users"
                        setInfo={setTiUsersInfo}
                        defaultValue={tiUsersInfo}
                        disabled={isEditDisabled}
                      ></FormRadio>
                    </div>
                    <div className="formItem radio-btn">
                      <FormRadio
                        radioData={radioDevice}
                        labelText="Device OS"
                        setInfo={setDeviceOSInfo}
                        defaultValue={deviceOSInfo}
                        disabled={isEditDisabled}
                      ></FormRadio>
                    </div>

                    <div className="formItem">
                      <Button
                        type="button"
                        variant="primary"
                        className="btn  save-button"
                        disabled={
                          isEditDisabled || isSubmitting || disableSubmitBtn
                        }
                        onClick={() => handleOpenEditModal()}
                      >
                        Save
                      </Button>
                      <Button
                        type="button"
                        variant="danger"
                        className="btn btn-danger save-button"
                        disabled={disableStopBtn}
                        onClick={() => handleOpenStopModal()}
                      >
                        Stop
                      </Button>
                      <Button
                        type="button"
                        variant="outline-secondary"
                        className="btn btn-secondary"
                        onClick={goBack}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </div>
      </div>
      <div className="notificationContainer">
        <Notification
          show={toast.isVisible}
          timer={4000}
          position="top-center" // Updated position for consistent positioning
          title={toast.title}
          description={toast.description}
          autohide={true}
          handleClose={handleNotificationClose}
          variant={toastVariant}
        />
      </div>
      <Modal
        enableModal={showStopModal}
        handleClose={handleCloseStopModal}
        handleSubmit={() => stopPushNotification()}
        title={"WARNING"}
        closeButtonText="Back"
        actionButtonText="Stop push notification"
        width="halfwidth"
        disableActionButton={disableStopBtn}
      >
        Are you sure you would like to <strong>stop</strong> this push
        notification? Once it has been stopped this{" "}
        <strong>cannot be reversed</strong>.
      </Modal>
      <Modal
        enableModal={showEditModal}
        handleClose={handleCloseEditModal}
        title={"Editing notification"}
        closeButtonText="Back"
        actionButtonText="Edit"
        width="halfwidth"
        buttonType="submit"
        form="PushForm"
        disableActionButton={disableSubmitBtn}
      >
        Are you sure you would like to edit this notification?
      </Modal>
      <Modal
        enableModal={showOneHourModal}
        handleClose={handleCloseOneHourModal}
        title={"Cannot edit notification"}
        closeButtonText="Back"
        width="halfwidth"
      >
        There is less than an hour until this push notification is scheduled to
        be sent. It can no longer be edited.
      </Modal>
      <Modal
        enableModal={showErrorModal}
        handleClose={goBack}
        title={"Sorry, something went wrong"}
        closeButtonText="Back"
        width="halfwidth"
      >
        Please try again later.
      </Modal>
    </Container>
  );
};
